import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import { forceTimezone } from 'utils';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import { Card, CollapseCard } from 'components/UI';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import { EditBusinessHoursRulesetsModal } from 'components/Modals';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { StyledCollapseList } from './RulesetsWidget.styled';
import { parseRulesHours, parseBusinessHoursData, defaultBusinessHoursData } from 'services/rules';

const timeSx = {
  color: 'text.primary',
  fontSize: '0.75rem',
};

const dateSx = {
  color: 'primary.main',
  fontSize: '0.8125rem',
  fontWeight: 500,
};

const cellSx = {
  padding: '8px 0',
  borderColor: '#E0E0E0',
};

const RulesetsTimes = ({ id }) => {
  const dispatch = useDispatch();
  const [openEditModal, setOpenEditModal] = useState(false);
  const { bhTimes } = useSelector((state) => state.vehiclesGroups);
  const { localeFormat } = useSelector((state) => state.organizations);

  const handleOpenEditModal = () => {
    setOpenEditModal((prevState) => !prevState);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('common.edit'),
      can: { name: 'vehicle_group', action: 'update' },
      onClick: handleOpenEditModal,
    },
  ];

  useEffect(() => {
    dispatch(VehiclesGroupsActions.fetchVehicleGroupsBHTimes(id));
  }, [dispatch, id]);

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      data-cy="bh-times-widget-card"
      title={i18n.t('vehicle_groups.business_hours.title')}
    >
      <Box>
        {bhTimes?.length > 0 ? (
          <>
            {bhTimes.map((rules, index) => (
              <StyledCollapseList key={index} data-cy="bh-times-widget-card-list">
                <CollapseCard title={rules?.business_hours_rules_set?.name}>
                  <Box display="flex" component="span" flexWrap="wrap" flexDirection="row">
                    {rules?.start_time && (
                      <Typography variant="body2" component="span" sx={timeSx}>
                        <span>{i18n.t('business_hours.effective_from')}</span>{' '}
                        <Box component="span" sx={dateSx}>
                          {forceTimezone(moment(rules?.start_time)).format(localeFormat)}
                        </Box>
                      </Typography>
                    )}
                    {rules?.end_time ? (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        sx={timeSx}
                      >
                        {i18n.t('common.to')}{' '}
                        <Box component="span" sx={dateSx}>
                          {forceTimezone(moment(rules?.end_time)).format(localeFormat)}
                        </Box>
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        sx={timeSx}
                      >
                        <span>{i18n.t('business_hours.effective_to')}</span>
                      </Typography>
                    )}
                  </Box>
                  <TableContainer>
                    <Table aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...cellSx, border: 0 }}>
                            {i18n.t('business_hours.day')}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, border: 0 }}>
                            {`${i18n.t('business_hours.from')} - ${i18n.t('business_hours.to')}`}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {defaultBusinessHoursData(
                          parseBusinessHoursData(rules?.business_hours_rules_set?.bh_rules)
                        ).map((rule, i) => (
                          <TableRow key={i}>
                            <TableCell sx={cellSx}>
                              <Typography key={i}>
                                <Box fontSize={14} component="span" fontWeight="fontWeightMedium">
                                  {i18n.t(`common.${rule.day}`)}
                                </Box>
                              </Typography>
                            </TableCell>
                            <TableCell sx={cellSx}>
                              {rule?.closed ? (
                                <Typography key={i}>
                                  <Box component="span" fontSize={13}>
                                    {i18n.t('business_hours.closed')}
                                  </Box>
                                </Typography>
                              ) : rule?.fullDay ? (
                                <Typography key={i}>
                                  <Box component="span" fontSize={13}>
                                    {i18n.t('business_hours.open')}
                                  </Box>
                                </Typography>
                              ) : (
                                <>
                                  {rule.hours.map((hours, i) => (
                                    <Typography key={i}>
                                      <Box component="span" fontSize={13}>
                                        {parseRulesHours(hours.start)} -{' '}
                                        {parseRulesHours(hours.end)}
                                      </Box>
                                    </Typography>
                                  ))}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CollapseCard>
              </StyledCollapseList>
            ))}
          </>
        ) : (
          <Typography color="textPrimary" style={{ lineHeight: 1 }}>
            <Box fontSize={14} component="span" lineHeight={1}>
              {i18n.t('common.empty_list')}
            </Box>
          </Typography>
        )}
      </Box>
      {openEditModal && (
        <EditBusinessHoursRulesetsModal
          open={true}
          currentVehicleGroup={{ id: id }}
          handleClick={handleOpenEditModal}
        />
      )}
    </Card>
  );
};

RulesetsTimes.propTypes = {
  id: PropTypes.number,
};

export default RulesetsTimes;
